import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { mediaList } from "@/mocks/media";
import type { MediaItem, MediaType } from "@/types/media";
import {
  GENRE_OPTIONS,
  PAGE_SIZE,
  REGIONS,
  SORT_OPTIONS,
  TYPE_DESCRIPTIONS,
  TYPE_LABELS,
  YEARS,
} from "@/constants/media";
import { useSeo } from "@/hooks/useSeo";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/base/EmptyState";

const VALID_TYPES: MediaType[] = ["tv", "movie", "variety"];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const mediaType: MediaType = VALID_TYPES.includes(type as MediaType)
    ? (type as MediaType)
    : "tv";

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("latest");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const typeLabel = TYPE_LABELS[mediaType];

  useSeo({
    title: `${typeLabel} - 星空传媒影视内容平台`,
    description: `${TYPE_DESCRIPTIONS[mediaType]}`,
    keywords: `星空传媒,${typeLabel},${typeLabel}推荐,${typeLabel}排行榜,影视内容`,
  });

  useEffect(() => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setVisibleCount(PAGE_SIZE);
  }, [mediaType]);

  const filtered = useMemo(() => {
    const list = (mediaList as MediaItem[]).filter((item) => item.type === mediaType);
    const result = list.filter((item) => {
      const matchGenre = genre === "全部" || item.genres.includes(genre);
      const matchRegion = region === "全部" || item.region === region;
      const matchYear = year === "全部" || String(item.year) === year;
      return matchGenre && matchRegion && matchYear;
    });
    return result.sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      if (sort === "rating") return b.rating - a.rating;
      return b.year - a.year || b.views - a.views;
    });
  }, [mediaType, genre, region, year, sort]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setVisibleCount(PAGE_SIZE);
  };

  const filterGroups = [
    { label: "题材", options: GENRE_OPTIONS, value: genre, onChange: setGenre },
    { label: "地区", options: REGIONS, value: region, onChange: setRegion },
    { label: "年份", options: YEARS, value: year, onChange: setYear },
  ];

  return (
    <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-accent-600 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span>分类</span>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{typeLabel}</span>
      </nav>

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          全部{typeLabel}
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-foreground-500">
          共收录 {filtered.length} 部{typeLabel}作品，支持按题材、地区、年份筛选，并按最新、热门、评分排序。
        </p>
      </header>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
        <aside className="w-full lg:w-64 lg:shrink-0">
          <button
            type="button"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="flex w-full items-center justify-between rounded-md border border-secondary-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-900 lg:hidden cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-accent-500"></i>
              筛选条件
            </span>
            <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>

          <div
            className={`${
              filtersOpen ? "block" : "hidden"
            } mt-3 rounded-lg border border-secondary-200 bg-background-50 p-4 lg:mt-0 lg:block`}
          >
            {filterGroups.map((group) => (
              <div key={group.label} className="mb-5 last:mb-0">
                <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-foreground-900">
                  <span className="h-3.5 w-1 rounded-full bg-accent-500" />
                  {group.label}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {group.options.map((option) => {
                    const active = group.value === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          group.onChange(option);
                          setVisibleCount(PAGE_SIZE);
                        }}
                        className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-colors cursor-pointer ${
                          active
                            ? "bg-accent-500 text-white"
                            : "bg-secondary-100 text-foreground-600 hover:bg-secondary-200"
                        }`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={resetFilters}
              className="mt-2 flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 py-2 text-xs font-medium text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-secondary-200 bg-background-50 px-4 py-3">
            <span className="text-sm text-foreground-500">
              共找到 <strong className="text-accent-600">{filtered.length}</strong> 个结果
            </span>
            <div className="flex items-center gap-1 rounded-full bg-secondary-100 p-1">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSort(option.value)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                    sort === option.value
                      ? "bg-background-50 text-accent-600 shadow-sm"
                      : "text-foreground-600 hover:text-accent-600"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {visible.length === 0 ? (
            <EmptyState
              icon="ri-file-search-line"
              title="没有找到符合条件的作品"
              description="试试更换筛选条件，或点击下方按钮重置全部筛选。"
              actionLabel="重置筛选条件"
              onAction={resetFilters}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 xl:grid-cols-4">
                {visible.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>

              <div className="mt-8 flex flex-col items-center gap-3">
                <p className="text-xs text-foreground-400">
                  已显示 {visible.length} / {filtered.length} 部作品
                </p>
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-600 cursor-pointer"
                  >
                    <i className="ri-add-line text-base"></i>
                    加载更多
                  </button>
                ) : (
                  <span className="text-xs text-foreground-400">已经到底啦</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}